import { Button, Col, Grid, Icon, NetworkIcon, Tooltip } from 'gu-ui-kit'
import React, { useEffect, useMemo, useState } from 'react'
import { Helmet } from 'react-helmet'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import useGeneratePathI18n from 'utils/hooks/useGeneratePathI18n'
import { Link } from 'react-router-dom'
import { useCancelToken } from 'utils/hooks/useCancelToken'
import { useGetConnectionLink } from 'utils/hooks/useGetConnectionLink'
import styles from 'modules/Services/Network/network.module.scss'
import AccountList from './subcomponents/AccountsList'
import useFiltersManager from 'utils/hooks/useFiltersManager'
import config from 'modules/Services/Network/subcomponents/AccountsList/filters'
import GridSpinner from 'components/UI/GridSpinner'
import SERVICES_API_V1 from 'api/services/v1'
import { useParams } from 'react-router'
import { useNetworksData } from 'modules/Services/hooks/useNetworksData'
import {
  FETCH_NETWORK_ACCOUNTS,
  FETCH_NETWORK_DATA,
  NETWORK_ACCOUNTS_CLEAN,
  SET_ACCOUNTS_FILTERS,
  SET_NETWORK_DATA,
  UPDATE_ACCOUNTS,
} from 'modules/Services/types'
import useTotallyEmptyList from 'utils/useTotallyEmptyList'
import omit from 'lodash/omit'
import EmptyResults from 'components/EmptyResults'
import NetworkItemHeader from 'modules/Services/Network/subcomponents/NetworkItemHeader'
import NetworkBalanceColumn from 'modules/Services/Network/subcomponents/NetworkBalanceColumn'
import { useBreakpoint } from 'utils/hoc/breakpointProvider'
import PropTypes from 'prop-types'

const LeftColumn = ({ children }) => {
  const breakpoints = useBreakpoint()

  return breakpoints.lg ? (
    <Col flexGrow={1}>{children}</Col>
  ) : (
    <Grid
      flexDirection={'column'}
      fluid
    >
      {children}
    </Grid>
  )
}

LeftColumn.propTypes = {
  children: PropTypes.node,
}

const Network = () => {
  const dispatch = useDispatch()
  const pathGen = useGeneratePathI18n()
  const { networkType, i18n } = useParams()
  const { t } = useTranslation(['services'])
  const { cancelableDispatch } = useCancelToken()
  const breakpoints = useBreakpoint()
  const connectionLink = useGetConnectionLink(networkType)

  const title = useMemo(
    () =>
      networkType
        ? `${t('common::nav_menu.advertisement_networks')} — ${t(
            'services::networks.' + networkType,
          )}`
        : t('common::nav_menu.advertisement_networks'),
    [networkType],
  )

  const {
    display_balance = true,
    loading,
    homepage_alias,
    has_account,
    accounts,
    filters,
  } = useSelector((state) => state['services/network'])

  const homepage_link = useMemo(() => {
    const lngSegment = i18n === 'ru' ? '' : `${i18n}/`

    return homepage_alias
      ? `${process.env.REACT_APP_PROTOCOL}://${process.env.REACT_APP_DOMAIN}/${lngSegment}networks/${homepage_alias}`
      : null
  }, [homepage_alias, i18n])

  const { networkId } = useNetworksData(networkType)

  const filterManager = useFiltersManager({
    filters,
    config: config(),
    setAction: SET_ACCOUNTS_FILTERS,
    // additionalFilterState: accountListConfig?.[networkType],
    localStorageKey: `services/network-${networkType}`,
  })

  useEffect(() => {
    if (networkId) {
      cancelableDispatch({
        type: FETCH_NETWORK_DATA,
        payload: {
          id: networkId,
        },
      })
    }
  }, [networkId])

  useEffect(() => {
    networkId &&
      SERVICES_API_V1.getNetworkSettings({ networkId })
        .then(({ response }) =>
          dispatch({
            type: SET_NETWORK_DATA,
            payload: {
              bulk_account_creation_allowed: response?.data?.bulk_account_creation_allowed,
            },
          }),
        )
        .catch((err) => console.logXHR(err))
  }, [networkId])

  const [totallyEmptyList, firstLoad] = useTotallyEmptyList(
    omit(filterManager.params, ['is_archived']), //игнорируем фильтр архива
    has_account ? [1] : [],
    !loading,
  )

  const [page, setPage] = useState(1)

  const { pagination, sorting, loading: accountsLoading } = accounts

  const composedParams = useMemo(
    () => ({
      ...(filterManager?.params || {}),
      sort: sorting.prop,
      direction: sorting.direction,
      offset: (page - 1) * pagination.limit,
      limit: pagination.limit,
      network_id: networkId,
    }),
    [filterManager.params, sorting, page, networkId],
  )

  useEffect(() => {
    dispatch({ type: NETWORK_ACCOUNTS_CLEAN })
  }, [sorting, filterManager.params, networkType])

  useEffect(() => {
    dispatch({
      type: UPDATE_ACCOUNTS,
      payload: { composedParams },
    })
  }, [composedParams])

  useEffect(() => {
    if (!composedParams.network_id || accountsLoading || !filterManager.params) {
      return
    }

    dispatch({
      type: FETCH_NETWORK_ACCOUNTS,
    })
  }, [filterManager.params, composedParams])

  return (
    <>
      <Helmet defer={false}>
        <title>{title}</title>
      </Helmet>

      {firstLoad ? (
        <GridSpinner
          width={32}
          className={'margin-top-40'}
        />
      ) : (
        <>
          <Grid
            gap={16}
            wrap={false}
          >
            <LeftColumn>
              <NetworkItemHeader
                title={title}
                totallyEmptyList={totallyEmptyList}
                networkType={networkType}
                networkId={networkId}
                homepage_link={homepage_link}
              />

              {totallyEmptyList ? (
                <div className={'layout-tile'}>
                  <EmptyResults
                    header={
                      <Grid
                        gap={16}
                        alignItems={'center'}
                      >
                        {t('services::networks.' + networkType)}
                        {homepage_link && (
                          <Tooltip
                            title={t(`services::about_network_tooltip`)}
                            placement={'top'}
                          >
                            <a
                              href={homepage_link}
                              target={'_blank'}
                              rel="noreferrer"
                              className={styles['info-tooltip__trigger']}
                            >
                              <Icon
                                size={'m'}
                                name={'information'}
                                color={'Interface-Seventh'}
                              />
                            </a>
                          </Tooltip>
                        )}
                      </Grid>
                    }
                    text={t('services::account_list.empty_list.text')}
                    image={
                      <NetworkIcon
                        name={networkType}
                        rounded
                      />
                    }
                    actionButton={
                      <Button
                        startIcon={<Icon name={'plus'} />}
                        component={Link}
                        to={pathGen(connectionLink, { networkType })}
                        variant={'primary'}
                        size={'l'}
                      >
                        {t('common::buttons.create_account')}
                      </Button>
                    }
                  />
                </div>
              ) : (
                <AccountList
                  className={'layout-tile'}
                  display_balance={display_balance}
                  networkId={networkId}
                  networkType={networkType}
                  filterManager={filterManager}
                  accounts={accounts}
                  setPage={setPage}
                />
              )}
            </LeftColumn>

            {breakpoints.lg && (
              <>
                {!totallyEmptyList && (
                  <Col
                    flexGrow={1}
                    className={styles['right-column']}
                  >
                    <NetworkBalanceColumn
                      className={'layout-tile'}
                      networkType={networkType}
                      homepage_link={homepage_link}
                    />
                  </Col>
                )}
              </>
            )}
          </Grid>
        </>
      )}
    </>
  )
}

export default Network
